import type {ApplicationKey} from '@/configs/proposition/propositionConfig.types';
import type {ValueOf} from '@myparcel-frontend/types';
import {Hosts, toApplicationKey} from '@/configs/proposition/propositionConfig.types';

/**
 * Get the config for the current domain.
 * If there are multiple chained subdomains, try to match the longest one.
 * If no config is found, return the default config.
 *
 * @param hostMapping - The mapping fetched by fetchMapping() above.
 * @param hostName - The hostname of the current page.
 * @returns The config key for the current page.
 */
export const hostNameMatcher = (
  hostName: `${string}.${ValueOf<typeof Hosts>}` | ValueOf<typeof Hosts>,
): ValueOf<typeof ApplicationKey> => {
  const applicationKey = toApplicationKey(hostName);

  if (applicationKey) {
    return applicationKey;
  }

  const slices = hostName.split('.');

  if (slices.length === 1) {
    throw new Error('No config found for hostname.');
  }

  const subHost = slices.splice((slices.length - 1) * -1);
  return hostNameMatcher(subHost.join('.') as ValueOf<typeof Hosts>);
};

/**
 * Get the hostname of the page. Overriden by environment variable if set.
 *
 * @returns The hostname of the page.
 */
export const getHostName = (): ValueOf<typeof Hosts> => {
  if (window?.location?.host) {
    return window.location.host as ValueOf<typeof Hosts>;
  }

  return Hosts.DEFAULT;
};

export const getApplicationKey = (): ValueOf<typeof ApplicationKey> => {
  const hostName = getHostName();
  return hostNameMatcher(hostName);
};
